import { AddCircleRounded } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Icon, IconButton, TextField, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { baseAxios } from "config";
import { useAppContextController } from "context/AppContext";
import useCompanyCache from "hooks/useCompanyCache";
import updateEvent from "layouts/pages/events/actions/updateEvent";
import useStyles from "layouts/pages/events/components/InterviewEditor/Attachments/styles";
import ImagePreviewModal from "layouts/pages/events/components/InterviewEditor/ImagePreviewModal";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useMutation, useQueryClient } from "react-query";
import { getCommonBaseImageUrl, getCompanyImageUrl } from "utils/helpers/upload";

const IMAGE_EXTENSIONS = ["jpeg", "jpg", "png", "webp"];

const ApplicantAttachmentsPreview = ({ currentApplicant, fileLocation = "attachments" }) => {
  const styles = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [files, setFiles] = useState([]);
  // const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const queryClient = useQueryClient();
  const { company, setCompany, setCompanyType } = useAppContextController();
  const { isLoadingCompany, refetchCompany } = useCompanyCache({
    company,
    setCompany,
    setCompanyType,
  });
  const IMAGE_SERVER = getCompanyImageUrl(company);

  const { firstName, lastName, attachments, _id } = currentApplicant;

  const [usePresigned, setUsePresigned] = useState([]);

  const changePresignedFlag = (idx, value) => {
    const newPresignedArray = [...usePresigned];
    newPresignedArray[idx] = value;
    setUsePresigned(newPresignedArray);
  };

  // Generate direct URL for the file
  const getDirectUrl = (file) => {
    return `${IMAGE_SERVER}/applicants/${currentApplicant?._id}/${file.type}/${
      file.filename || file.name
    }`;
  };

  // Determine file preview source
  const getFilePreviewSrc = (file) => {
    if (IMAGE_EXTENSIONS.includes(file.docType.toLowerCase())) {
      return usePresigned && file.presignedUrl ? file.presignedUrl : getDirectUrl();
    }

    // Show icon for PDFs and Word docs
    const baseUrl = getCommonBaseImageUrl(company);
    if (file.docType.toLowerCase() === "pdf") {
      return `${baseUrl}/static/pdf-icon.png`;
    }
    if (file.docType.toLowerCase() === "docx") {
      return `${baseUrl}/static/word-icon.png`;
    }
    if (file.docType.toLowerCase() === "xlsx") {
      return `${baseUrl}/static/excel_icon.png`;
    }

    return null;
  };

  // Handle file opening
  const handleFileOpen = (file, idx) => {
    if (usePresigned && file.presignedUrl) {
      fetch(file.presignedUrl, { method: "HEAD" })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Presigned URL failed");
          }
          window.open(file.presignedUrl);
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.log("Presigned URL failed, falling back to direct URL");
          changePresignedFlag(false, idx);
          window.open(getDirectUrl());
        });
    } else {
      window.open(getDirectUrl());
    }
  };

  useEffect(() => {
    if (attachments?.length) {
      setFiles(
        attachments.map((file) => {
          const {
            docType,
            filename: name,
            title,
            type,
            uploadDate,
            file: attachmentFile,
            presignedUrl,
          } = file;
          return { docType, name, title, type, presignedUrl };
        })
      );
      setUsePresigned(
        attachments.map((file) => {
          return !!file.presignedUrl;
        })
      );
    } else {
      setFiles([]);
      setUsePresigned([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  return (
    <MDBox className={styles.otherSection}>
      <MDBox display="flex" justifyContent="flex-start" className={styles.titleBar}>
        <MDTypography component="label" variant="button" fontWeight="bold" color="info">
          <span style={{ color: "#fb8c00" }}>{`${firstName} ${lastName} `}</span>
          ATTACHMENTS
        </MDTypography>
      </MDBox>
      <Grid container spacing={3} alignItems="center">
        <Grid item sm={10.5}>
          <MDBox className={styles.section_images_wapper}>
            {files.map((file, idx) => {
              return (
                <MDBox
                  className={styles.section_image_container}
                  key={file ? file.name : `filename_${idx}`}
                >
                  <Grid
                    container
                    direction="column"
                    justifyContent="space-evenly"
                    alignItems="center"
                  >
                    <Grid>
                      {["jpeg", "jpg", "png", "gif", "heic", "jfif", "svg"].includes(
                        file?.docType?.toLowerCase()
                      ) && (
                        <MDBox>
                          <img
                            src={getFilePreviewSrc(file)}
                            alt="previews"
                            className={styles.section_image}
                            onError={() => changePresignedFlag(idx, false)}
                          />
                        </MDBox>
                      )}
                      {file?.docType?.toLowerCase() === "pdf" && (
                        <MDBox
                          onClick={() => {
                            handleFileOpen(file, idx);
                          }}
                        >
                          <img
                            src={`${getCommonBaseImageUrl(company)}/static/pdf-icon.png`}
                            alt="preview"
                            width={100}
                            height={100}
                          />
                        </MDBox>
                      )}
                      {file?.docType?.toLowerCase() === "docx" && (
                        <MDBox
                          onClick={() => {
                            handleFileOpen(file, idx);
                          }}
                        >
                          <img
                            src={`${getCommonBaseImageUrl(company)}/static/word-icon.png`}
                            alt="preview"
                            width={100}
                            height={100}
                          />
                        </MDBox>
                      )}
                    </Grid>

                    <Grid>
                      <TextField
                        defaultValue={file?.name?.substring(0, file?.name?.lastIndexOf("."))}
                        inputProps={{ min: 0, style: { textAlign: "center" } }}
                        value={files[idx].editedName?.substring(
                          0,
                          file?.editedName?.lastIndexOf(".")
                        )}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </MDBox>
              );
            })}
          </MDBox>
        </Grid>
      </Grid>
      {/* <ImagePreviewModal
        imagePreviewOpen={imagePreviewOpen}
        setImagePreviewOpen={setImagePreviewOpen}
        currentInterview={currentInterview}
        previewFile={previewFile}
      /> */}
    </MDBox>
  );
};

export default ApplicantAttachmentsPreview;
